import get from "lodash/get";
import { googleLogout } from "@react-oauth/google";

export const getHeadline = (story) => {
  if (!story) {
    return null;
  }

  return get(story, ["alternative", "home", "default", "headline"]) || story.headline;
};

export {
  formatStoryDate,
  handleTimeFormat,
  formatStoryDateOnly,
  formatLogoDate,
  toISTIsoString,
  convertTimestampToYYYYMMDDHHMM,
  formatLiveBlogTime,
  formatLiveBlogDate,
  getStoryTimestamp,
  getRelativeTimeInfo,
  formatRelativeTime
} from "./date-utils";

export const storyPageBodyAd = (story) => {
  const searchParams = new URLSearchParams(window.location.search);
  const utmSource = searchParams.get("utm_source");
  const ele = document.querySelector("#text-element-with-ad p");
  if (
    ele &&
    story?.["story-template"] !== "pv-exclusive" &&
    story?.["story-template"] !== "exclusive-story-without-ad"
  ) {
    const adText = document.createElement("p");
    adText.textContent = "ADVERTISEMENT";
    adText.id = "ad-text";
    ele.appendChild(adText);

    const desktopAd = document.createElement("div");
    desktopAd.id = utmSource === "izooto" ? "div-gpt-ad-1711630758806-0" : "div-gpt-ad-1675837175211-0";
    desktopAd.className = "hide-mobile story-body-ad";

    const mobileAd = document.createElement("div");
    mobileAd.id = utmSource === "izooto" ? "div-gpt-ad-1711631172416-0" : "div-gpt-ad-1675837516069-0";
    mobileAd.className = "hide-desktop story-body-ad";
    ele.append(desktopAd);
    ele.append(mobileAd);
  }
};
export const extractTextFromHtml = (htmlString) => {
  const re = /<[^>]*>/g;
  const text = htmlString.replace(re, "");
  return text.replace(/&nbsp;/g, " ") || "";
};
export function setAuthCookie(name, value, expiryDate) {
  if (typeof window === "undefined" || !name) return;
  const isProduction = process.env.NODE_ENV === "production";
  const secureStr = isProduction ? "; Secure" : "";

  let cookieString = `${name}=${encodeURIComponent(value)}; Path=/; Domain=.prajavani.net${secureStr}; SameSite=Lax`;
  if (expiryDate instanceof Date && !isNaN(expiryDate.getTime()) && expiryDate > new Date()) {
    cookieString += `; Expires=${expiryDate.toUTCString()}`;
  }
  document.cookie = cookieString;
}

export function getAuthCookie(name) {
  if (typeof window === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  if (match) {
    return decodeURIComponent(match[2]);
  }
  return null;
}

export function removeAuthCookie(name) {
  if (typeof window === "undefined" || !name) return;
  const isProduction = process.env.NODE_ENV === "production";
  const secureStr = isProduction ? "; Secure" : "";

  document.cookie = `${name}=; Path=/; Domain=.prajavani.net; Expires=Thu, 01 Jan 1970 00:00:00 GMT${secureStr}; SameSite=Lax`;
}

export function clearAuthCookies() {
  removeAuthCookie("st-access-token");
  removeAuthCookie("st-refresh-token");
}

export function getJwtExpiry(token) {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length === 3) {
      let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
      while (base64.length % 4) {
        base64 += "=";
      }
      const payload = JSON.parse(atob(base64));
      if (payload && typeof payload.exp === "number") {
        return new Date(payload.exp * 1000);
      }
    }
  } catch (e) {
    // Silence noisy warning for non-JWT tokens (like refresh tokens)
  }
  return null;
}

export function getAccessToken() {
  return getAuthCookie("st-access-token");
}

export function getRefreshToken() {
  return getAuthCookie("st-refresh-token");
}

export function initializeAuthSync() {
  if (typeof window === "undefined") return false;
  const stAccessToken = getAuthCookie("st-access-token");
  const stRefreshToken = getAuthCookie("st-refresh-token");

  if (stRefreshToken) {
    if (localStorage.getItem("refresh_token") !== stRefreshToken) {
      localStorage.setItem("refresh_token", stRefreshToken);
    }
    if (stAccessToken && localStorage.getItem("access_token") !== stAccessToken) {
      localStorage.setItem("access_token", stAccessToken);
    }
    return true;
  } else {
    // Cookies are missing, meaning user logged out or session expired. Clear Local Storage.
    if (localStorage.getItem("access_token") || localStorage.getItem("refresh_token")) {
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("user");
      localStorage.removeItem("commentsToken");
    }
    return false;
  }
}

export function addTokensToLocalStorage(access_token, refresh_token) {
  if (window) {
    const cleanAccessToken = access_token.replaceAll(" ", "+");
    const cleanRefreshToken = refresh_token.replaceAll(" ", "+");

    // 1. Write directly to the cookies first
    const accessExpiry = getJwtExpiry(cleanAccessToken);
    setAuthCookie("st-access-token", cleanAccessToken, accessExpiry);

    const refreshExpiry = getJwtExpiry(cleanRefreshToken);
    setAuthCookie("st-refresh-token", cleanRefreshToken, refreshExpiry);

    // Also write qt-auth cookie from subscribe-landing
    document.cookie = [`qt-auth=${encodeURIComponent(cleanAccessToken)}`, "Path=/", "Secure", "SameSite=Lax"].join(
      "; "
    );

    // 2. Then populate Local Storage
    localStorage.setItem("access_token", cleanAccessToken);
    localStorage.setItem("refresh_token", cleanRefreshToken);
  }
}

export function getUserDetails() {
  const url = "/get-user-details";
  const timestamp = Date.now();
  // Adding a timestamp to the URL to ensure it's always unique and server treats it as new request.
  const newUrl = `${url}?timestamp=${timestamp}`;

  const access_token = getAccessToken();

  if (access_token?.length > 0) {
    return fetch(newUrl, { headers: new Headers({ Authorization: `Bearer ${access_token}` }) })
      .then(async (response) => {
        if (response?.status === 401) {
          return await refreshAccessToken(getUserDetails)
            .then((res) => {
              return res;
            })
            .catch((err) => {
              return err;
            });
        } else {
          return response.json();
        }
      })
      .then((userdata) => {
        return userdata;
      })
      .catch((err) => {
        return err;
      });
  } else {
    const refresh_token = getRefreshToken();
    if (refresh_token?.length > 0) {
      return refreshAccessToken(getUserDetails);
    }
  }
}
export function putUserDetails(data) {
  const url = "/put-user-details";

  const access_token = getAccessToken();

  if (access_token?.length > 0) {
    return fetch(url, {
      headers: new Headers({ Authorization: `Bearer ${access_token}`, "Content-Type": "application/json" }),
      method: "POST",
      body: JSON.stringify(data)
    })
      .then(async (response) => {
        if (response?.status === 401) {
          return await refreshAccessToken(() => putUserDetails(data))
            .then((res) => {
              return res;
            })
            .catch((err) => {
              return err;
            });
        } else {
          return response.json();
        }
      })
      .then((userdata) => {
        return userdata;
      })
      .catch((err) => {
        return err;
      });
  } else {
    const refresh_token = getRefreshToken();
    if (refresh_token?.length > 0) {
      return refreshAccessToken(() => putUserDetails(data));
    }
  }
}

let refreshTokenPromise = null;

export function refreshAccessToken(callbackFunction) {
  const url = "/refresh-token";

  const refresh_token = getRefreshToken();

  if (refresh_token?.length > 0) {
    if (!refreshTokenPromise) {
      console.log("[AUTH] Initiating new /refresh-token network request...");
      refreshTokenPromise = fetch(url, {
        method: "POST",
        headers: new Headers({ Authorization: `Bearer ${refresh_token}` })
      })
        .then((response) => {
          if (!response.ok) {
            console.error(`[AUTH] /refresh-token failed with status ${response.status} ${response.statusText}`);
            throw new Error(JSON.stringify({ status: response.status, statusText: response.statusText }));
          } else {
            return response.json();
          }
        })
        .then((tokendata) => {
          console.log("[AUTH] Refresh request succeeded.");
          addTokensToLocalStorage(tokendata?.["st-access-token"], tokendata?.["st-refresh-token"]);
        })
        .finally(() => {
          console.log("[AUTH] Refresh token network request completed. Releasing lock.");
          refreshTokenPromise = null;
        });
    } else {
      console.log("[AUTH] Refresh already in progress. Waiting for existing promise...");
    }

    return refreshTokenPromise
      .then(() => {
        console.log("[AUTH] Existing request retried after successful refresh.");
        return callbackFunction();
      })
      .catch((err) => {
        console.error("[AUTH] Refresh token flow encountered an error:", err);
        console.log("[AUTH] Session termination initiated due to refresh failure.");

        if (typeof window !== "undefined") {
          if (window.logoutInProgress) {
            console.log("[AUTH] Manual logout in progress. Skipping duplicate termination redirect.");
            return;
          }
          try {
            window.localStorage.removeItem("user");
            window.localStorage.removeItem("access_token");
            window.localStorage.removeItem("refresh_token");
            window.localStorage.removeItem("commentsToken");
            document.cookie = "qt-auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax";

            try {
              googleLogout();
            } catch (e) {
              console.error("[AUTH] Google logout error:", e);
            }

            const ssoEndpoint =
              window.store?.getState?.()?.qt?.config?.["publisher-attributes"]?.sso_endpoint ||
              window.qtState?.config?.["publisher-attributes"]?.sso_endpoint ||
              "https://sso.prajavani.net";

            const redirectUrl = window.location.href.split("?")[0];

            if (!window.location.pathname.includes("/auth/logout")) {
              window.location.href = `${ssoEndpoint}/auth/logout?redirectUrl=${encodeURIComponent(redirectUrl)}`;
            }
          } catch (e) {
            console.error("[AUTH] Error during session termination:", e);
          }
        }

        let catchError;
        try {
          // for error thrown if status code is not 200
          catchError = JSON.parse(err?.message);
        } catch (error) {
          catchError = err;
        }
        return catchError;
      });
  }
}

export function generateTokenForCommenting(userObject) {
  return fetch("/generate-comments-token", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(userObject)
  })
    .then((response) => response.json())
    .then((data) => {
      return data;
    })
    .catch((error) => {
      console.log(error);
    });
}

export function signInUpWithGoogleOneTap(id_token) {
  const signInBody = {
    thirdPartyId: "google",
    clientType: "web",
    oAuthTokens: {
      id_token
    }
  };

  return fetch("/sign-in-with-token", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      rid: "thirdpartypasswordless"
    },
    body: JSON.stringify(signInBody)
  })
    .then((response) => response.json())
    .then((tokendata) => {
      addTokensToLocalStorage(tokendata?.["st-access-token"], tokendata?.["st-refresh-token"]);
    })
    .catch((error) => {
      console.log(error);
    });
}

export function sendChangeEmailOtp(email, redirectUrl) {
  const access_token = getAccessToken();
  if (!access_token) return Promise.reject("No token");

  return fetch("/change-email-otp", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
      redirecturl: redirectUrl
    },
    body: JSON.stringify({ email })
  }).then(async (response) => {
    if (response?.status === 401) {
      return await refreshAccessToken(() => sendChangeEmailOtp(email, redirectUrl));
    }
    return response.json();
  });
}

export function sendChangePhoneOtp(phoneNumber, redirectUrl) {
  const access_token = getAccessToken();
  if (!access_token) return Promise.reject("No token");

  return fetch("/change-phone-otp", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`,
      redirecturl: redirectUrl
    },
    body: JSON.stringify({ phoneNumber })
  }).then(async (response) => {
    if (response?.status === 401) {
      return await refreshAccessToken(() => sendChangePhoneOtp(phoneNumber, redirectUrl));
    }
    return response.json();
  });
}

export function verifySsoCode(preAuthSessionId, deviceId, userInputCode) {
  const access_token = getAccessToken();
  if (!access_token) return Promise.reject("No token");

  return fetch("/verify-sso-code", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`
    },
    body: JSON.stringify({ preAuthSessionId, deviceId, userInputCode })
  }).then(async (response) => {
    if (response?.status === 401) {
      return await refreshAccessToken(() => verifySsoCode(preAuthSessionId, deviceId, userInputCode));
    }
    return response.json();
  });
}

export function getEpaperEditions() {
  return fetch("https://api-epaper-prod.deccanherald.com/epaper/editions?publisher=PV", {
    method: "GET",
    headers: {
      accept: "application/json"
    }
  }).then((response) => response.json());
}

export function getEpaperPreferences() {
  const access_token = getAccessToken();
  if (!access_token) {
    console.warn("[E-Paper API] No access_token found for GET preferences.");
    return Promise.resolve(null);
  }
  return fetch("https://api-epaper-prod.deccanherald.com/users/preferences?publisher=PV", {
    method: "GET",
    headers: {
      accept: "application/json",
      Authorization: `Bearer ${access_token}`
    }
  })
    .then(async (response) => {
      if (response.ok) {
        const data = await response.json();
        return data;
      }
      console.error("[E-Paper API] GET Preferences Failed with status:", response.status);
      return null;
    })
    .catch((error) => {
      console.error("[E-Paper API] GET Preferences Error:", error);
      return null;
    });
}

export function updateEpaperPreferences(editionId) {
  const access_token = getAccessToken();
  if (!access_token) {
    console.error("[E-Paper API] No access_token found for PATCH preferences.");
    return Promise.reject("No token");
  }

  const payload = {
    pv_preferred_edition_id: editionId,
    welcome_popup_enabled: true
  };

  console.log("[E-Paper API] Updating preferences with payload:", payload);

  return fetch("https://api-epaper-prod.deccanherald.com/users/preferences", {
    method: "PATCH",
    headers: {
      accept: "*/*",
      "Content-Type": "application/json",
      Authorization: `Bearer ${access_token}`
    },
    body: JSON.stringify(payload)
  }).then(async (response) => {
    if (response.ok) {
      const data = await response.json();
      return data;
    }
    const errText = await response.text();
    console.error("[E-Paper API] PATCH Preferences Failed. Status:", response.status, "Response:", errText);
    throw new Error("Failed to update preferences");
  });
}
